import {nsSyncFields} from "../../../req/nsSyncFields";
import {IExportTemplate, IHistory, ISyncstep, ISystem, ISystemRole, ITableColumn} from "../../../index";
import {THistory_key, THistory_value} from "../../object/history/index";
import {ObjectTypeId} from "../../../constants/ObjectTypeId";
import {
    getObjectDefinition,
    getRawSyncFieldValue,
    IObjectDefinition,
    isNullOrUndefined,
    isNullOrUndefinedOrEmptyString
} from "../../../util/misc";
import {getHash} from "../../../constants/RouteDefinitions";
import {nsTooltip} from "../../../util/nsTooltip";
import {lm} from "../../../appContext";
import {HIERARCHY_ARROW} from "../../../constants";
import {nsHistory} from "../../object/history/nsHistory";
import {getDateTime} from "../../../util/datetime";
import TObjectData_basic = nsSyncFields.TObjectData_basic;

interface IParams {
    syncsteps: ISyncstep[]
    departments: TObjectData_basic[]
    systems: ISystem[]
    exportTemplates?: IExportTemplate[] | undefined
    column: ITableColumn<IHistory>
    history: IHistory
    searchkey: KnockoutObservable<string>
}

interface IHistoryDerived {
    link?: string | undefined
    text: string
    title?: string | undefined
}

type THistoryDerived = IHistoryDerived | false | null;

export class DerivedValue {
    private readonly departments: nsSyncFields.TObjectData_basic[]
    private readonly systems: ISystem[]
    private readonly exportTemplates: IExportTemplate[] | undefined
    private readonly column: ITableColumn<IHistory>
    private readonly history: IHistory
    private readonly key: THistory_key
    private readonly val: THistory_value
    private readonly objecttypeid: ObjectTypeId
    private readonly objDef: IObjectDefinition
    private readonly searchkey: KnockoutObservable<string>

    constructor(private params: IParams) {
        this.departments = params.departments
        this.systems = params.systems
        this.exportTemplates = params.exportTemplates
        this.column = params.column
        this.history = params.history

        this.objecttypeid = this.history.objecttypeid
        this.objDef = getObjectDefinition(this.objecttypeid)!
        this.key = this.column.key as THistory_key
        this.val = this.history[this.key]

        this.searchkey = params.searchkey
    }

    private _getStr_raw() {
        let str = String(this.val)
        if (this.key == 'displayname') {
            str = str || String(this.history.userid)
        }
        if (isNullOrUndefinedOrEmptyString(str)) {
            return ''
        }
        switch (this.key) {
            case 'registered': {
                str = getDateTime(str, true)
                break
            }
            case 'syncstepid': {
                str = this.getSyncstepName(Number(this.val))
                break
            }
        }
        return str
    }

    private getStr_raw() {
        return this.getSearchMarkup(this.searchkey(), this._getStr_raw())
    }

    private getStr_derived(cls: string = 'derived-value'): string {
        if (isNullOrUndefinedOrEmptyString(this._getStr_raw())) {
            return ''
        }
        const obj = this.getObj_derived()
        if (obj === false) {
            return `<span class="derived-value"><span>N/A</span></span>`
        }
        if (obj == null) {
            return ''
        }
        const derived = obj;
        const searchMarkup = this.getSearchMarkup(this.searchkey(), derived.text);
        const inner = derived.link
            ? `<a href="${derived.link}">${searchMarkup}</a>`
            : `<span>${searchMarkup}</span>`
        ;
        return `<span class="${cls}" ${derived.title ? `title="${derived.title}"`: ''}>${inner}</span>`
    }

    private getObj_derived(): THistoryDerived {
        const strRaw = this._getStr_raw();
        if (isNullOrUndefinedOrEmptyString(strRaw)) {
            return null
        }
        const num = parseInt(String(strRaw))
        const str = String(strRaw)
        switch (this.key) {
            case 'displayname': {
                const link = getHash(this.objDef.routeDetails!, {id: this.history.userid}) ?? undefined
                const text = str
                return {link, text}
            }
            case 'oldvalue':
            case 'newvalue': {
                switch (this.history.attributename) {
                    case 'SystemRoleId': {
                        if (!num) {
                            return null
                        }
                        let system: ISystem | undefined
                        let role: ISystemRole | undefined
                        this.systems.forEach(s => s.roles.forEach(r => {
                            if (r.id == num) {
                                system = s
                                role = r
                                return
                            }
                        }))
                        if (!system || !role) {
                            return false
                        }
                        const link = getHash('systemaccessdetails', {id: system.id}) ?? undefined
                        const text = `${system.name} ${HIERARCHY_ARROW} ${role.name}`;
                        const title = lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? ''
                        return {link, text, title}
                    }
                    case 'ExportTemplateId': {
                        if (!num || !this.exportTemplates?.length) {
                            return null
                        }
                        const exporttemplate = this.exportTemplates.find(t => t.id == num)
                        if (!exporttemplate) {
                            return false
                        }
                        const link = getHash('synctemplatedetails', {id: exporttemplate.id}) ?? undefined
                        const text = exporttemplate.name;
                        const title = lm.t(nsTooltip.getTitle_exportTemplate(exporttemplate) ?? '') ?? ''
                        return {link, text, title}
                    }
                }
                return null
            }
            case 'departmentid': {
                if (!num) {
                    return null
                }
                const department = this.departments.find(d => d.id == num)
                if (!department) {
                    return false
                }
                const link = getHash('departmentdetails', {id: department.id}) ?? undefined
                const text = getRawSyncFieldValue(getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName ?? [], department.syncfields) ?? ''
                const title = lm.t(nsTooltip.getTitle_objectdata(department) ?? '') ?? ''
                return {link, text, title}
            }
            case 'comment': {
                const regex_rule = nsHistory.getRegExpComment_rule()
                regex_rule.lastIndex = 0
                const isRule = regex_rule.test(String(str))
                if (isRule) {
                    regex_rule.lastIndex = 0
                    const regResult = regex_rule.exec(str)
                    if (!regResult) {
                        return null
                    }
                    const id = regResult[2];
                    if (id === '0') {
                        return null
                    }
                    const link = getHash('rulesetdetails', {id: id}) ?? undefined;
                    const text = regResult[1] ?? '';
                    const title = regResult[1] ?? '';
                    return {link, text, title}
                }

                const regex_user = nsHistory.getRegExpComment_user()
                regex_user.lastIndex = 0
                const isUser = regex_user.test(String(str))
                if (isUser) {
                    regex_user.lastIndex = 0
                    const regResult = regex_user.exec(str)
                    if (!regResult) {
                        return null
                    }
                    const id = regResult[2];
                    if (id === '0') {
                        return null
                    }
                    const isWithoutName = regResult[1] === 'with'
                    const link = getHash('userdetails', {id: id}) ?? undefined;
                    const text = isWithoutName ? `User ${id}`: (regResult[1] ?? '');
                    const title = regResult[1] ?? '';
                    return {link, text, title}
                }
                return null
            }
            case "syncstepid": {
                return {text: str}
            }
        }
        return null
    }

    public getStr(segment: '_raw' |'raw' | 'derived' | 'full' | 'title'): string {
        if (segment === '_raw') {
            return this._getStr_raw()
        }
        if (segment === 'title') {
            const obj = this.getObj_derived();
            if (obj && typeof obj === 'object') {
                return obj.title ?? ''
            } else {
                return this._getStr_raw()
            }
        }
        switch (this.key) {
            case 'displayname': {
                return this.getStr_derived('')
            }
            case 'syncstepid': {
                return this.getStr_raw()
            }
            default: {
                switch (segment) {
                    case "raw":
                        return this.getStr_raw()
                    case "derived":
                        return this.getStr_derived()
                    case "full":
                        return [this.getStr_raw(), this.getStr_derived()]
                            .filter(Boolean)
                            .join(' ')
                }
            }
        }
    }

    private getSearchMarkup(needle: string, haystack: any) {
        if (!needle) {
            return String(haystack)
        }
        const _needle = this.replaceHtmlTags(needle);
        let _haystack: string = this.replaceHtmlTags(String(haystack));
        if (_needle) {
            _haystack = _haystack.replace(new RegExp(_needle, 'gi'), (match: string) => `<mark>${match}</mark>`);
        }
        return _haystack;
    }

    private replaceHtmlTags(str: string): string {
        if (!str) {
            return str
        }
        return str
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            ;
    }

    private getSyncstepName(syncstepid: number): string {
        return this.params.syncsteps?.find(o => o.id == syncstepid)?.name || String(syncstepid);
    }

}